import { useEffect, useRef, useState } from 'react';
import {
  TripRecorder,
  EventWindow,
  type DetectorEvent,
  type GpsSample,
  type UploadEvent,
} from '../sensors';
import {
  createAccelSource,
  PermissionDeniedError,
} from '../platform/accel-source';
import {
  createGpsSource,
  GpsPermissionDeniedError,
  GpsUnavailableError,
} from '../platform/gps-source';
import { getQueue, subscribeStats, flushNow } from '../platform/upload-service';
import { logMyEvent } from '../platform/my-events-log';
import { HomeRadiusGuard, type GuardCounters } from '../platform/home-radius-guard';
import type { UploadStats } from '../platform/uploader';
import { deviceClass } from '../platform/device-class';
import { detectRuntime, isIos } from '../platform/runtime';
import {
  checkPermissions,
  getLastPermError,
  howToReEnable,
  requestLocation,
  requestMotion,
  type PermissionState,
  type PermissionStatus,
} from '../platform/permissions';
import { acquireWakeLock, releaseWakeLock, type WakeLockResult } from '../platform/wake-lock';
import { setRecording as setGlobalRecordingState } from '../platform/recording-state';
import { BUILD_TS } from '../build-info';

/**
 * Trip recorder.
 *
 * Permissions are surfaced explicitly with Allow buttons rather than
 * relying on the start-click side-effect — users were getting denied
 * prompts without a clear path to recovery. Once both location and
 * motion are granted, Start becomes available.
 *
 * Uploads go through the application-level singleton uploader so
 * queued events survive closing this page, going offline, etc.
 */
export function RecordPage() {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'idle' | 'starting' | 'running' | 'stopping'>('idle');
  const [hitCount, setHitCount] = useState(0);
  const [swerveCount, setSwerveCount] = useState(0);
  const [gps, setGps] = useState<GpsSample | null>(null);
  const [stats, setStats] = useState<UploadStats>({
    uploaded: 0,
    pending: 0,
    lastError: null,
    lastSuccessTs: null,
  });
  const [perms, setPerms] = useState<PermissionStatus | null>(null);
  const [diagnostics, setDiagnostics] = useState<string[]>([]);
  /** Measured accel sample rate over the last second. Surfaced in the
   *  diagnostics panel so we can spot WebView throttling regressions
   *  without leaving the app. */
  const [accelRateHz, setAccelRateHz] = useState<number | null>(null);
  const [wakeLockInfo, setWakeLockInfo] = useState<WakeLockResult | null>(null);
  const log = (msg: string) =>
    setDiagnostics((d) => [`${new Date().toISOString().slice(11, 19)} ${msg}`, ...d].slice(0, 20));

  const accelRef = useRef(createAccelSource());
  const gpsRef = useRef(createGpsSource());
  const recorderRef = useRef<TripRecorder | null>(null);
  /** Rolling-GUID window: enriches each event with eventGuid + tsRelMs.
   *  Persists across Start/Stop within the same page mount, so a quick
   *  re-Start within 10s of the last event extends the previous window. */
  const windowRef = useRef<EventWindow>(new EventWindow());
  /** Home-radius privacy guard: trims events near trip start and end
   *  before they reach the upload queue. Created per trip in start(). */
  const guardRef = useRef<HomeRadiusGuard | null>(null);
  const [guardStats, setGuardStats] = useState<GuardCounters | null>(null);
  /** Mutable accel-sample counter, sampled once per second by an interval. */
  const accelSampleCountRef = useRef(0);
  const accelRateTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Pull current permission state on mount + re-check on visibility return
  // (user may have toggled in Settings while away).
  //
  // Merge is monotonic: once a sensor reports 'granted', a subsequent
  // 'prompt' from the same source can't downgrade it. This stops the
  // iOS Safari flicker where navigator.permissions.query returns
  // 'prompt' for ~1s after the user actually granted, which combined
  // with the visibility-fired recheck was wiping the granted pill.
  useEffect(() => {
    const merge = (incoming: PermissionStatus) =>
      setPerms((p) => {
        if (!p) return incoming;
        return {
          location: monotonic(p.location, incoming.location),
          motion: monotonic(p.motion, incoming.motion),
        };
      });
    void checkPermissions().then(merge);
    const onVisible = () => {
      if (!document.hidden) void checkPermissions().then(merge);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Subscribe to upload stats from the application-level singleton
  useEffect(() => subscribeStats(setStats), []);

  // Warn the user before they close / reload / navigate away mid-trip.
  // The browser shows a generic "Leave site?" prompt — text we provide
  // is ignored by every modern browser per spec, but the existence of
  // a returnValue is what triggers the prompt. iOS Safari ignores
  // beforeunload entirely; on Android/desktop this gives the user one
  // last chance to tap Stop before losing the in-flight trip.
  useEffect(() => {
    if (!running) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required for the prompt to actually show in older Chrome.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [running]);

  const onLocationRequest = async () => {
    const next = await requestLocation();
    setPerms((p) => (p ? { ...p, location: next } : p));
  };
  const onMotionRequest = async () => {
    const next = await requestMotion();
    setPerms((p) => (p ? { ...p, motion: next } : p));
  };

  const allGranted =
    perms !== null &&
    (perms.location === 'granted' || perms.location === 'unsupported') &&
    (perms.motion === 'granted' || perms.motion === 'unsupported');

  const start = async () => {
    setError(null);
    if (!allGranted) {
      setError('Grant location and motion access before starting.');
      return;
    }
    setPhase('starting');
    log('start: tapped');
    try {
      // Fresh privacy guard per trip — origin is captured on the
      // first GPS fix and the buffer is discarded on stop.
      guardRef.current = new HomeRadiusGuard();
      setGuardStats(guardRef.current.stats());

      const enqueueReleased = (released: UploadEvent[]) => {
        for (const ev of released) {
          void getQueue().enqueue(ev);
          logMyEvent({ eventGuid: ev.eventGuid, lat: ev.lat, lon: ev.lon });
        }
      };

      const onEvent = (e: DetectorEvent) => {
        if (e.kind === 'hit') setHitCount((n) => n + 1);
        else setSwerveCount((n) => n + 1);
        // Strip absolute timestamp; replace with (eventGuid, tsRelMs)
        // from the rolling window. The absolute ts NEVER leaves this
        // device — privacy model relies on it.
        const { eventGuid, tsRelMs } = windowRef.current.enrich(e.ts);
        const { ts: _absTs, ...rest } = e;
        const upload: UploadEvent = { ...rest, eventGuid, tsRelMs };
        // Hand to the guard. Events near origin are dropped; events
        // near the moving "current position" are buffered until the
        // device has moved past them. Only released events reach the
        // queue and the local own-events log.
        const released = guardRef.current?.submit(upload) ?? [upload];
        enqueueReleased(released);
        if (guardRef.current) setGuardStats(guardRef.current.stats());
      };
      recorderRef.current = new TripRecorder({
        deviceClass: deviceClass(),
        sampleRateHz: accelRef.current.nominalRateHz,
        onEvent,
      });

      log('start: requesting motion stream');
      accelSampleCountRef.current = 0;
      await accelRef.current.start((s) => {
        accelSampleCountRef.current += 1;
        recorderRef.current?.ingestAccel(s);
      });
      log('start: motion ✓');
      // Tick the rate read-out once per second from the sample counter.
      // Setting state at sample rate would flood React; this is one
      // setState/sec and gives us a clear "are we getting 50 Hz?" answer
      // in diagnostics.
      accelRateTimerRef.current = setInterval(() => {
        const count = accelSampleCountRef.current;
        accelSampleCountRef.current = 0;
        setAccelRateHz(count);
      }, 1000);

      // Keep the screen awake — on Android the WebView is throttled or
      // suspended once the screen dims. Best-effort: failure here doesn't
      // stop the trip, just means recording may degrade with screen-off.
      const wakeLockResult = await acquireWakeLock();
      setWakeLockInfo(wakeLockResult);
      log(`wake-lock: ${wakeLockResult.via} ${wakeLockResult.held ? '✓' : '✕'}${wakeLockResult.detail ? ' ' + wakeLockResult.detail : ''}`);
      try {
        log('start: requesting first GPS fix (≤30s)…');
        await gpsRef.current.start(
          (s) => {
            setGps(s);
            recorderRef.current?.ingestGps(s);
            // Feed the guard. First call captures the origin; subsequent
            // calls may release buffered events that have now been left
            // behind by ≥ the trim radius.
            if (guardRef.current) {
              const released = guardRef.current.updateGps(s);
              for (const ev of released) {
                void getQueue().enqueue(ev);
                logMyEvent({ eventGuid: ev.eventGuid, lat: ev.lat, lon: ev.lon });
              }
              setGuardStats(guardRef.current.stats());
            }
          },
          (e) => {
            log(`gps runtime error: ${e.message}`);
            setError(`GPS lost during trip: ${e.message}`);
          },
        );
        log('start: GPS ✓ first fix received');
      } catch (gpsErr) {
        await accelRef.current.stop();
        throw gpsErr;
      }

      setRunning(true);
      setPhase('running');
      // Mark recording globally — version-sync defers any pending
      // bundle reload until the trip ends.
      setGlobalRecordingState(true);
      log('start: trip running');
    } catch (e) {
      setPhase('idle');
      log(`start failed: ${e instanceof Error ? e.message : String(e)}`);
      // Re-probe permissions in case the failure was a permission change
      // since mount (e.g. user denied at the prompt).
      void checkPermissions().then(setPerms);

      if (e instanceof PermissionDeniedError) {
        setError(
          'Motion permission denied. Use the Allow button below — iOS requires the prompt to come from your tap.',
        );
      } else if (e instanceof GpsPermissionDeniedError) {
        setError(
          "Location permission denied. Use the Allow button below — joltstream can't record a trip without your location.",
        );
      } else if (e instanceof GpsUnavailableError) {
        setError(
          `Location unavailable: ${e.message}. Make sure GPS is enabled on your device and try again outdoors.`,
        );
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  };

  const stop = async () => {
    setPhase('stopping');
    log('stop: tapped');
    if (accelRateTimerRef.current) {
      clearInterval(accelRateTimerRef.current);
      accelRateTimerRef.current = null;
    }
    setAccelRateHz(null);
    await accelRef.current.stop();
    await gpsRef.current.stop();
    await releaseWakeLock();
    setWakeLockInfo(null);
    // Discard buffered near-end events — they never moved out of the
    // trim radius, so they stay on-device and are forgotten.
    if (guardRef.current) {
      const { droppedEndTrim } = guardRef.current.reset();
      if (droppedEndTrim > 0) {
        log(`stop: discarded ${droppedEndTrim} near-end event${droppedEndTrim === 1 ? '' : 's'}`);
      }
      setGuardStats(guardRef.current.stats());
    }
    // Force an immediate upload of whatever's still queued.
    await flushNow();
    setRunning(false);
    setPhase('idle');
    // Clear the global recording flag — if a bundle update arrived
    // mid-trip, version-sync will reload the page now.
    setGlobalRecordingState(false);
    log('stop: done');
  };

  const isCapacitor = detectRuntime() === 'capacitor';

  return (
    <div className="page record-page">
      {isCapacitor && (
        <div className="app-brand-line">
          <img src="/favicon.svg" alt="" width="28" height="28" aria-hidden="true" />
          <strong>joltstream</strong>
        </div>
      )}

      <h1>{running ? 'Streaming' : 'Stream Data'}</h1>

      {!isCapacitor && isIos() && !running && (
        <div className="banner banner-warn">
          <strong>iOS limitation:</strong> keep the screen on and this tab in focus.
          Sensors stop when the tab is backgrounded. (We'll fix this in a future native build — see <a href="/about">About</a>.)
        </div>
      )}

      {error && <div className="banner banner-err">{error}</div>}

      {!running && (
        <section className="permissions">
          {!perms ? (
            <>
              <h2>Permissions</h2>
              <p className="hint">Checking…</p>
            </>
          ) : allGranted ? (
            // Both granted → collapse into a one-line summary by default.
            // Native <details> defaults to closed; the user can expand to
            // see the per-permission detail (and the "How to fix" hint
            // pre-populated for any sensor that's "unsupported").
            <details className="permissions-collapsed">
              <summary>
                <span className="permissions-summary-label">Permissions</span>
                <span
                  className="permissions-summary-ticks"
                  aria-label="Both permissions granted"
                >
                  <span className="permissions-summary-tick">
                    <span aria-hidden="true">📍</span>
                    <span className="check check-ok">✓</span>
                  </span>
                  <span className="permissions-summary-tick">
                    <span aria-hidden="true">📳</span>
                    <span className="check check-ok">✓</span>
                  </span>
                </span>
              </summary>
              <PermissionRow
                icon="📍"
                label="Location"
                description="Tags each event with where it happened"
                state={perms.location}
                onRequest={onLocationRequest}
              />
              <PermissionRow
                icon="📳"
                label="Motion sensors"
                description="Detects bumps and swerves"
                state={perms.motion}
                onRequest={onMotionRequest}
              />
            </details>
          ) : (
            <>
              <h2>Permissions</h2>
              <PermissionRow
                icon="📍"
                label="Location"
                description="Tags each event with where it happened"
                state={perms.location}
                onRequest={onLocationRequest}
              />
              <PermissionRow
                icon="📳"
                label="Motion sensors"
                description="Detects bumps and swerves"
                state={perms.motion}
                onRequest={onMotionRequest}
              />
              <p className="hint">
                joltstream needs both sensors. Nothing leaves your device
                until you tap Start.
              </p>
            </>
          )}
        </section>
      )}

      <div className="trip-controls">
        {!running ? (
          <button
            className="primary big"
            onClick={start}
            disabled={!allGranted || phase === 'starting'}
            title={!allGranted ? 'Grant location and motion first' : undefined}
          >
            {phase === 'starting' ? 'Starting…' : 'Start Journey'}
          </button>
        ) : (
          <button className="big" onClick={stop} disabled={phase === 'stopping'}>
            {phase === 'stopping' ? 'Stopping…' : 'Stop'}
          </button>
        )}
      </div>

      <div className="stat-grid">
        <Stat label="Hits this trip" value={hitCount} />
        <Stat label="Swerves this trip" value={swerveCount} />
        <Stat
          label="Speed"
          value={gps && gps.speedKmh >= 0 ? `${gps.speedKmh.toFixed(0)} km/h` : '—'}
        />
        <Stat label="GPS accuracy" value={gps ? `${gps.accuracyM.toFixed(0)} m` : '—'} />
        <Stat label="Pending upload" value={stats.pending} />
        <Stat label="Uploaded this session" value={stats.uploaded} />
      </div>

      {stats.pending > 0 && !navigator.onLine && (
        <p className="hint" style={{ color: 'goldenrod' }}>
          Offline — {stats.pending} events queued. They'll upload automatically when you're back online.
        </p>
      )}
      {stats.lastError && (
        <p className="hint" style={{ color: 'crimson' }}>
          Upload note: {stats.lastError}
        </p>
      )}

      <details className="diagnostics">
        <summary>Diagnostics ({detectRuntime()})</summary>
        <ul>
          <li>Build: {BUILD_TS}</li>
          <li>Runtime: {detectRuntime()}</li>
          <li>Capacitor global: {String(typeof (window as unknown as { Capacitor?: unknown }).Capacitor !== 'undefined')}</li>
          <li>Phase: {phase}</li>
          <li>Online: {String(navigator.onLine)}</li>
          <li>Location permission: {perms?.location ?? 'checking'}</li>
          <li>Motion permission: {perms?.motion ?? 'checking'}</li>
          <li>Last perm error: {getLastPermError() ?? '—'}</li>
          <li>Last GPS: {gps ? `${gps.lat.toFixed(4)},${gps.lon.toFixed(4)} (±${gps.accuracyM.toFixed(0)}m)` : '—'}</li>
          <li>Accel rate: {accelRateHz === null ? '—' : `${accelRateHz} Hz (declared ${accelRef.current.nominalRateHz} Hz)`}</li>
          <li>Wake lock: {wakeLockInfo ? `${wakeLockInfo.via} ${wakeLockInfo.held ? '✓' : '✕'}${wakeLockInfo.detail ? ` (${wakeLockInfo.detail})` : ''}` : '—'}</li>
          <li>
            Privacy trim (75 m):{' '}
            {guardStats
              ? `${guardStats.released} released · ${guardStats.pending} held · ${guardStats.droppedOrigin} near start · ${guardStats.droppedEndTrim} near finish · ${guardStats.droppedNoFix} no-fix`
              : '—'}
          </li>
        </ul>
        {diagnostics.length > 0 && (
          <pre className="diagnostics-log">{diagnostics.join('\n')}</pre>
        )}
      </details>

      {isCapacitor && (
        <p className="hint" style={{ marginTop: '2rem', textAlign: 'center' }}>
          <a href="/about">About joltstream</a>
        </p>
      )}
    </div>
  );
}

function PermissionRow({
  icon,
  label,
  description,
  state,
  onRequest,
}: {
  icon: string;
  label: string;
  description: string;
  state: PermissionState;
  onRequest: () => void;
}) {
  return (
    <div className={`perm-row perm-${state}`}>
      <span className="perm-icon" aria-hidden="true">{icon}</span>
      <div className="perm-row-info">
        <div className="perm-row-label">
          {label}{' '}
          <span className="perm-status" aria-label={`status: ${state}`}>
            {state === 'granted' && <span className="check check-ok">✓</span>}
            {state === 'denied' && <span className="check check-err">✕</span>}
            {state === 'prompt' && <span className="check check-warn">?</span>}
            {state === 'unsupported' && <span className="check check-muted">–</span>}
          </span>
        </div>
        <div className="perm-row-desc">
          {state === 'granted' && 'Ready'}
          {state === 'denied' && 'Blocked — see below'}
          {state === 'prompt' && description}
          {state === 'unsupported' && 'Not available on this device'}
        </div>
      </div>
      <div className="perm-row-action">
        {state === 'prompt' && (
          <button className="primary" onClick={onRequest}>
            Allow
          </button>
        )}
        {state === 'denied' && (
          <details className="perm-blocked">
            <summary>How to fix</summary>
            <p>
              {howToReEnable(label === 'Location' ? 'location' : 'motion')}
            </p>
          </details>
        )}
      </div>
    </div>
  );
}

/**
 * Don't downgrade a permission from 'granted' to 'prompt' on re-check.
 * iOS Safari's navigator.permissions API briefly reports 'prompt' even
 * after a successful grant; without this guard, the visibilitychange
 * re-check would erase the green tick immediately.
 *
 * Genuine denial does propagate (granted → denied is allowed) so a
 * user revoking in Settings is still respected.
 */
function monotonic(prev: PermissionState, next: PermissionState): PermissionState {
  if (prev === 'granted' && next === 'prompt') return prev;
  return next;
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}
